import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Option, OptionType, OptionValue } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import OptionChoicesWithPopover from 'modules/menu/options/components/OptionChoicesWithPopover';

import OptionBindingActions from './OptionBindingActions';
import { MenuLocaleContext } from '../../../MenuLocaleContext';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';

type Props = {
  className?: string;
  disableHoverStyle: boolean;
  hidden: boolean;
  itemId: string;
  onToggleOpen: () => void;
  option: Option;
  optionBindingId: string;
};

const optionTypes: { [key in OptionType]: string } = {
  bool: 'menu-option.switch',
  choice: 'menu-option.single-choice',
  multi_choice: 'menu-option.multi-choice',
};

const HEADER_BREAKPOINT = 800;

const Wrapper = styled.div`
  /* Set wrapper background color to solid color so hover style doesn't make it transparent */
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  cursor: pointer;
`;

const Header = styled.div<{ disableHoverStyle: boolean; hidden: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 104px;
  padding: 24px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  transition: background-color 100ms;

  ${({ disableHoverStyle }) =>
    !disableHoverStyle &&
    css`
      &:hover {
        /* ignore-color */
        background-color: ${colors.pepper8};
      }
    `}

  ${({ hidden }) =>
    hidden &&
    css`
      opacity: 0;
    `}
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  margin-inline-end: 16px;
  overflow: hidden;

  @media (min-width: ${HEADER_BREAKPOINT}px) {
    margin-inline-end: 32px;
  }

  @media (width >= 1000px) {
    margin-inline-end: 64px;
  }

  @media (width >= 1200px) {
    margin-inline-end: 120px;
  }
`;

const Right = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
`;

const NameAndChoices = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${HEADER_BREAKPOINT}px) {
    flex-direction: row;
  }
`;

const NameAndTags = styled.div`
  display: flex;
  margin-block-end: 4px;
  align-items: flex-start;
  flex-direction: column;

  @media (min-width: ${HEADER_BREAKPOINT}px) {
    flex-direction: row;
    align-items: center;
  }
`;

const Name = styled(T.Body3).attrs({ fontWeight: 'bold' })`
  text-overflow: ellipsis;
  overflow: hidden;
`;

const OptionTypeTag = styled(Tag).attrs({ size: 'small', uppercase: true, variant: 'lightBlack' })`
  margin-block-start: 4px;

  @media (min-width: ${HEADER_BREAKPOINT}px) {
    margin-inline-start: 12px;
    margin-block-start: 0;
  }
`;

const DisabledChoicesTag = styled(Tag).attrs({ size: 'small', uppercase: true, variant: 'lightRed' })`
  margin-block-start: 4px;

  @media (min-width: ${HEADER_BREAKPOINT}px) {
    margin-inline-start: 12px;
    margin-block-start: 0;
  }
`;

const OptionBindingHeader = ({
  className,
  disableHoverStyle,
  hidden,
  itemId,
  onToggleOpen,
  option,
  optionBindingId,
}: Props) => {
  const menuPermissions = useContext(MenuPermissionsContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const numOfDisabledChoices = option.values.reduce((acc: number, choice: OptionValue) => {
    return choice.enabled === false ? acc + 1 : acc;
  }, 0);

  return (
    <Wrapper className={className} onClick={onToggleOpen}>
      <Header disableHoverStyle={disableHoverStyle} hidden={hidden}>
        <Left>
          <NameAndChoices>
            <NameAndTags>
              <Name dir="auto" lang={primaryLanguage}>
                {localize(option.name, primaryLanguage)}
              </Name>
              <Tags>
                <OptionTypeTag>
                  <FormattedMessage id={optionTypes[option.type]} />
                </OptionTypeTag>
                {numOfDisabledChoices > 0 && (
                  <DisabledChoicesTag>
                    <FormattedMessage
                      id="menu-option.disabled-choices-with-count"
                      values={{ count: numOfDisabledChoices }}
                    />
                  </DisabledChoicesTag>
                )}
              </Tags>
            </NameAndTags>

            <OptionChoicesWithPopover
              onClick={onToggleOpen}
              optionChoices={option.values}
              primaryLanguage={primaryLanguage}
            />
          </NameAndChoices>
        </Left>

        {menuPermissions.contentEdit && (
          <Right>
            <OptionBindingActions itemId={itemId} optionBindingId={optionBindingId} />
          </Right>
        )}
      </Header>
    </Wrapper>
  );
};

export default OptionBindingHeader;
